// Queen Dollar: a standalone USD spending tracker (Admin only). Each record keeps the NPR rate it was entered with; the
// default rate only pre-fills new records. Delete hides a record (kept, recoverable). Nothing here touches Accounts or Sales.
import { useState, useEffect, useCallback, useMemo } from "react";
import { PageHeader, StatGrid, FinanceStatCard, DataTable, Modal, Field, Input, TextArea, Button, Notice, ActionMenu, Select,
    SkeletonTiles, SkeletonTable, StatusBadge } from "../ui/kit/components";
import { text, fmtDay, fmtDateTime } from "../ui/kit/tokens";
import { loadQd, saveRecord, deleteRecord, restoreRecord, setDefaultRate, errText } from "./api";
import { nprOf, usdError, rateError, fmtUsd, fmtNpr, fmtRate, ktmToday } from "./calc";

const newRequestId = () => (crypto.randomUUID ? crypto.randomUUID()
    : "10000000-1000-4000-8000-100000000000".replace(/[018]/g, c => (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)));

function RecordForm({ initial, defaultRate, onClose, onSaved }) {
    const editing = !!initial?.id;
    const [f, setF] = useState(() => editing
        ? { record_date: initial.record_date, usd_amount: String(Number(initial.usd_amount)), npr_rate: String(Number(initial.npr_rate)), remarks: initial.remarks || "" }
        : { record_date: ktmToday(), usd_amount: "", npr_rate: String(Number(defaultRate)), remarks: "" });
    const [requestId] = useState(newRequestId);     // one id per form: a repeated Save can never add a second record
    const [busy, setBusy] = useState(false), [err, setErr] = useState("");
    const set = (k) => (e) => setF(s => ({ ...s, [k]: e.target.value }));
    const today = ktmToday();
    const dateErr = !f.record_date ? "Choose the date." : f.record_date > today ? "The date can't be in the future." : "";
    const uErr = f.usd_amount === "" ? "" : usdError(f.usd_amount), rErr = rateError(f.npr_rate);
    const remErr = f.remarks.length > 500 ? "At most 500 characters." : "";
    const npr = nprOf(f.usd_amount, f.npr_rate);
    const invalid = !!(dateErr || usdError(f.usd_amount) || rErr || remErr);
    const save = async () => {
        setBusy(true); setErr("");
        try {
            await saveRecord({ ...(editing ? { id: initial.id } : { client_request_id: requestId }), record_date: f.record_date,
                usd_amount: f.usd_amount.trim(), npr_rate: f.npr_rate.trim(), remarks: f.remarks.trim() });
            onSaved();
        } catch (e) { setErr(errText(e)); setBusy(false); }
    };
    return (
        <Modal open onClose={onClose} title={editing ? "Edit record" : "Add record"}
            subtitle={editing ? "The record keeps its own NPR rate unless you change it here." : "The NPR rate starts at the default; change it for this record if needed."}
            footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button onClick={save} disabled={busy || invalid}>{busy ? "Saving…" : "Save"}</Button></>}>
            <div style={{ display: "grid", gap: 14 }}>
                <Field label="Date" required error={dateErr}><Input type="date" value={f.record_date} max={today} onChange={set("record_date")} /></Field>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 14 }}>
                    <Field label="USD spent" required error={uErr}><Input inputMode="decimal" placeholder="0.00" value={f.usd_amount} onChange={set("usd_amount")} autoFocus /></Field>
                    <Field label="NPR rate (per USD)" required error={rErr}><Input inputMode="decimal" value={f.npr_rate} onChange={set("npr_rate")} /></Field>
                </div>
                <div style={{ ...text.caption, display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                    <span>NPR equivalent (USD × rate)</span>
                    <strong style={{ fontSize: 16, color: "inherit" }} data-testid="qd-npr-preview">{npr ? fmtNpr(npr) : "—"}</strong>
                </div>
                <Field label="Description / remarks" error={remErr} hint={`${f.remarks.length}/500`}><TextArea value={f.remarks} onChange={set("remarks")} /></Field>
                {err && <Notice tone="danger">{err}</Notice>}
            </div>
        </Modal>
    );
}

function RateForm({ current, onClose, onSaved }) {
    const [v, setV] = useState(String(Number(current)));
    const [busy, setBusy] = useState(false), [err, setErr] = useState("");
    const e1 = rateError(v);
    const save = async () => {
        setBusy(true); setErr("");
        try { await setDefaultRate(v.trim()); onSaved(); } catch (e) { setErr(errText(e)); setBusy(false); }
    };
    return (
        <Modal open onClose={onClose} title="Default NPR rate" subtitle="Used to pre-fill new records only. Existing records keep the rate they were entered with."
            footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={busy || !!e1}>{busy ? "Saving…" : "Save rate"}</Button></>}>
            <div style={{ display: "grid", gap: 14 }}>
                <Field label="NPR per USD" required error={e1}><Input inputMode="decimal" value={v} onChange={e => setV(e.target.value)} autoFocus /></Field>
                {err && <Notice tone="danger">{err}</Notice>}
            </div>
        </Modal>
    );
}

function Confirm({ title, body, confirmLabel, tone = "danger", onClose, onConfirm }) {
    const [busy, setBusy] = useState(false), [err, setErr] = useState("");
    const go = async () => { setBusy(true); setErr(""); try { await onConfirm(); } catch (e) { setErr(errText(e)); setBusy(false); } };
    return (
        <Modal open onClose={onClose} title={title}
            footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button variant={tone === "danger" ? "danger" : "primary"} onClick={go} disabled={busy}>{busy ? "Working…" : confirmLabel}</Button></>}>
            <div style={{ display: "grid", gap: 12 }}>
                <div>{body}</div>
                {err && <Notice tone="danger">{err}</Notice>}
            </div>
        </Modal>
    );
}

export default function QueenDollarPage() {
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [view, setView] = useState("active");
    const [form, setForm] = useState(null);          // null | {} (new) | record
    const [rateOpen, setRateOpen] = useState(false);
    const [confirm, setConfirm] = useState(null);     // { kind: "delete" | "restore", row }
    const reload = useCallback(async () => {
        try { setErr(""); setData(await loadQd()); } catch (e) { setErr(errText(e)); }
    }, []);
    useEffect(() => { reload(); }, [reload]);
    const rows = useMemo(() => (data ? data.records.filter(r => (view === "deleted") === !!r.deleted_at) : []), [data, view]);
    const deletedCount = data ? data.records.filter(r => r.deleted_at).length : 0;
    const s = data?.summary;
    const done = async () => { setForm(null); setRateOpen(false); setConfirm(null); await reload(); };

    return (
        <div style={{ maxWidth: 1100 }}>
            <PageHeader title="Queen Dollar" subtitle="USD spending tracker. Separate from Accounts and Sales: nothing here creates vouchers, journal entries or bank changes."
                actions={data && <Button onClick={() => setForm({})}>Add record</Button>} />
            {err && <div style={{ marginBottom: 16 }}><Notice tone="danger">{err}</Notice></div>}
            {!data && !err ? <><SkeletonTiles count={3} /><SkeletonTable rows={6} cols={5} /></> : !data ? null : <>
                <StatGrid>
                    <FinanceStatCard label="Total USD spent" value={fmtUsd(s.total_usd)} sub={`${s.record_count} record${Number(s.record_count) === 1 ? "" : "s"}`} />
                    <FinanceStatCard label="Total NPR equivalent" value={fmtNpr(s.total_npr)} sub="Each record at its own rate" />
                    <FinanceStatCard label="Default NPR rate" value={fmtRate(data.settings?.default_rate)}
                        sub={<button type="button" onClick={() => setRateOpen(true)} style={{ background: "none", border: 0, padding: 0, color: "inherit", textDecoration: "underline", cursor: "pointer", font: "inherit" }}>Change default rate</button>} />
                </StatGrid>
                <DataTable rows={rows} searchText={r => `${r.record_date} ${r.remarks || ""} ${r.usd_amount}`} searchPlaceholder="Search remarks or amounts…"
                    empty={view === "deleted" ? "No deleted records." : "No records yet. Add the first USD spend."}
                    filters={<Select value={view} onChange={e => setView(e.target.value)} style={{ width: 170 }} aria-label="Show">
                        <option value="active">Records</option><option value="deleted">Deleted ({deletedCount})</option></Select>}
                    onRowClick={view === "active" ? (r => setForm(r)) : undefined}
                    columns={[
                        { key: "record_date", header: "Date", sortValue: r => r.record_date + r.created_at, render: r => fmtDay(r.record_date) },
                        { key: "usd_amount", header: "USD", align: "right", sortValue: r => Number(r.usd_amount), render: r => <span style={text.money}>{fmtUsd(r.usd_amount)}</span> },
                        { key: "npr_rate", header: "Rate", align: "right", sortValue: r => Number(r.npr_rate), render: r => fmtRate(r.npr_rate), hideOnMobile: true },
                        { key: "npr_amount", header: "NPR", align: "right", sortValue: r => Number(r.npr_amount), render: r => <span style={text.money}>{fmtNpr(r.npr_amount)}</span> },
                        { key: "remarks", header: "Remarks", render: r => r.remarks ? <span style={{ overflowWrap: "anywhere" }}>{r.remarks}</span> : "—", hideOnMobile: true },
                        ...(view === "deleted" ? [{ key: "deleted_at", header: "Deleted", render: r => <StatusBadge status="off" label={fmtDateTime(r.deleted_at)} /> }] : []),
                    ]}
                    rowActions={r => <ActionMenu items={r.deleted_at
                        ? [{ label: "Restore", onClick: () => setConfirm({ kind: "restore", row: r }) }]
                        : [{ label: "Edit", onClick: () => setForm(r) }, { label: "Delete", onClick: () => setConfirm({ kind: "delete", row: r }) }]} />} />
            </>}
            {form && <RecordForm initial={form.id ? form : null} defaultRate={data.settings?.default_rate ?? 153} onClose={() => setForm(null)} onSaved={done} />}
            {rateOpen && <RateForm current={data.settings?.default_rate ?? 153} onClose={() => setRateOpen(false)} onSaved={done} />}
            {confirm && <Confirm
                title={confirm.kind === "delete" ? "Delete this record?" : "Restore this record?"}
                confirmLabel={confirm.kind === "delete" ? "Delete" : "Restore"} tone={confirm.kind === "delete" ? "danger" : "primary"}
                body={<>{fmtDay(confirm.row.record_date)} · {fmtUsd(confirm.row.usd_amount)} · {fmtNpr(confirm.row.npr_amount)}
                    <div style={{ ...text.caption, marginTop: 6 }}>{confirm.kind === "delete"
                        ? "It leaves the totals and the list, and stays under Deleted, where an Admin can restore it."
                        : "It returns to the list and the totals."}</div></>}
                onClose={() => setConfirm(null)}
                onConfirm={async () => { await (confirm.kind === "delete" ? deleteRecord : restoreRecord)(confirm.row.id); await done(); }} />}
        </div>
    );
}
